import { VoiceStatus } from "@next-platform/packages-ts-types";
import {
  ButtonIcon,
  ButtonVariant,
  DropDownMenu,
  IconVariants,
  ListEmpty,
  ListItem,
  ListItemBadgeVariant,
  ListItemVariant,
  Text,
} from "@next-platform/client-common-spicy-ui";
import { UserVoice, useTranslatedLanguage, useTranslation } from "@next-platform/client-common-core";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { ISO_TO_NAME } from "@/components/search/data";
import Trans from "@/components/Trans/Trans";
import { FilterKeys } from "@/components/type";
import { ROUTES, TEN_SECONDS } from "@/constants";
import useToast from "@/hooks/useToast";
import { TabNames } from "@/pages/ChatbotMy/types";
import { getEmptyStateValue } from "@/utils";

import getVoiceStatus from "./getVoiceStatus";

type VoiceListContainerProps = {
  voices: UserVoice[];
  onDeleteVoice?: (voice: UserVoice) => void;
  onRefresh?: () => void;
  search: string;
};

type DropdownMenuParams = {
  voice: UserVoice;
  navigate: NavigateFunction;
  onDeleteVoice?: (voice: UserVoice) => void;
  t: TFunction;
};

function getDropdownMenuItems({ voice, navigate, onDeleteVoice, t }: DropdownMenuParams) {
  const { status } = voice;
  const isGenerating = status !== undefined && getVoiceStatus(status) === ListItemBadgeVariant.GENERATING;
  const isGeneratingOrError = !status || isGenerating || status === VoiceStatus.FAILED;

  const onPressPreview = () => navigate(ROUTES.voiceClonePreview.replace(":cloneId", voice.id));
  const onPressRemove = () => onDeleteVoice?.(voice);
  const onPressEdit = () => navigate(`${ROUTES.voice}/${voice.id}?edit=true`);

  const items = [
    ...(!isGeneratingOrError
      ? [
          {
            label: t("myCreation:card.actions.preview"),
            icon: IconVariants.PLAY,
            onPress: onPressPreview,
          },
        ]
      : []),
    ...(!isGenerating
      ? [
          {
            label: t("myCreation:card.actions.remove"),
            icon: IconVariants.TRASH,
            onPress: onPressRemove,
          },
        ]
      : []),
  ];

  if (voice.status === VoiceStatus.READY_TO_USE)
    items.unshift({
      label: t("myCreation:card.actions.edit"),
      icon: IconVariants.EDIT,
      onPress: onPressEdit,
    });

  return items;
}

type VoiceListItemCardProps = {
  voice: UserVoice;
  menuItems: ReturnType<typeof getDropdownMenuItems>;
  onItemClick(): void;
};

const START_ACTION_CLASS_NAMES = { icon: "fill-black stroke-black dark:fill-white dark:stroke-white" };

const VoiceListItemCard = ({ voice, menuItems, onItemClick }: VoiceListItemCardProps) => {
  const { name, id, status, description, language } = voice;
  const isGenerating = status !== undefined && getVoiceStatus(status) === ListItemBadgeVariant.GENERATING;
  const { getTranslatedLanguage } = useTranslatedLanguage();
  const languageTag = useMemo(() => {
    const languageName = ISO_TO_NAME[language ?? ""];
    return languageName ? getTranslatedLanguage(languageName) : language;
  }, [getTranslatedLanguage, language]);
  const startAction = useMemo(
    () => ({
      ...(isGenerating ? { loading: true } : { iconStart: IconVariants.PLAY }),
      className: "!bg-gray-5 opacity-100 !border-transparent size-[42px]",
      classNames: START_ACTION_CLASS_NAMES,
      onClick: onItemClick,
    }),
    [isGenerating, onItemClick],
  );

  const endAction = useMemo(
    () =>
      menuItems.length > 0
        ? {
            children: (
              <DropDownMenu listItems={menuItems} placement="bottom-end">
                <ButtonIcon
                  variant={ButtonVariant.TRANSPARENT}
                  icon={IconVariants.THREE_DOTS}
                  className="w-5 h-5 text-gray-10 hover:text-gray-12 cursor-pointer flex-shrink-0"
                />
              </DropDownMenu>
            ),
          }
        : undefined,
    [menuItems],
  );

  return (
    <div className="grow basis-[352px] min-w-0" key={id}>
      <ListItem
        className="bg-gray-3 h-[74px] min-w-0 [&>div:first-child]:min-w-0 [&>div:first-child>div:last-child]:min-w-0 [&>div:first-child>div:last-child>div:first-child]:min-w-0 [&_p]:max-w-full [&_p]:[overflow-wrap:anywhere]"
        label={name || id || "Custom voice"}
        subLabel={description}
        onClick={onItemClick}
        variant={ListItemVariant.BORDERED}
        badge={!status || status === VoiceStatus.READY_TO_USE ? undefined : getVoiceStatus(status)}
        languageTag={status === VoiceStatus.READY_TO_USE ? languageTag : ""}
        subLabelMaxLines={2}
        startAction={startAction}
        endAction={endAction}
      />
    </div>
  );
};

const VoiceListContainer = ({ voices, onDeleteVoice, onRefresh, search = "" }: VoiceListContainerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { displayToast } = useToast();

  useEffect(() => {
    if (!onRefresh) return;
    const id = setInterval(() => {
      onRefresh();
    }, TEN_SECONDS);
    return () => clearInterval(id);
  }, [onRefresh]);

  const onClick = useCallback(
    (voice: UserVoice) => {
      switch (voice.status) {
        case VoiceStatus.READY_FOR_PROCESSING:
        case VoiceStatus.PROCESSING:
        case VoiceStatus.PENDING_UPLOAD:
          displayToast({ message: "Your custom voice is currently being generated. Please come back in a couple of minutes." });
          break;
        case VoiceStatus.READY_FOR_PREVIEW:
          navigate(`${ROUTES.voice}/clone-preview/${voice.id}`);
          break;
        case VoiceStatus.FAILED:
          navigate(`${ROUTES.voice}/error/${voice.id}`);
          break;
        default:
          navigate(`${ROUTES.voice}/${voice.id}`);
          break;
      }
    },
    [displayToast, navigate],
  );

  const createHandleClick = useCallback(
    (voice: UserVoice) => () => {
      onClick(voice);
    },
    [onClick],
  );

  if (!voices.length)
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <ListEmpty
          isLoading={false}
          {...getEmptyStateValue({
            searchedQuery: search,
            filterBy: FilterKeys.ALL,
            navigate,
            activeTab: TabNames.VOICES,
            t,
          })}
        />
      </div>
    );

  return (
    <>
      <Text className="text-gray-10 text-xs h-9 mt-2.5 mb-lg">
        <Trans i18nKey="common:results.found" values={{ count: voices.length }} />
      </Text>
      <div className="w-full h-full flex flex-wrap mob:grid grid-cols-[repeat(auto-fill,minmax(356px,1fr))] items-start content-start gap-2.5">
        {voices.map((voice: UserVoice) => (
          <VoiceListItemCard
            key={voice.id}
            voice={voice}
            menuItems={getDropdownMenuItems({ voice, navigate, onDeleteVoice, t })}
            onItemClick={createHandleClick(voice)}
          />
        ))}
      </div>
    </>
  );
};
export default VoiceListContainer;
